import React from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

export const BrandLogo = ({
    size = 'medium',
    isLight = false,
    showText = true,
    to = '/interview-prep'
}) => {
    const dimensions = {
        small: { height: 24, iconSize: 24, fontSize: '1rem' },
        medium: { height: 32, iconSize: 32, fontSize: '1.25rem' },
        large: { height: 44, iconSize: 44, fontSize: '1.75rem' }
    };

    const current = dimensions[size];
    const navigate = useNavigate();

    const handleClick = () => {
        navigate(to);
    };

    return (
        <Box 
            onClick={handleClick}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: current.height, cursor: 'pointer' }}
        >
            <motion.svg
                width={current.iconSize}
                height={current.iconSize}
                viewBox="0 0 100 100"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                initial="initial"
                animate="animate"
                whileHover="hover"
            >
                {/* Background "Flow" Circle - Subtle glow */}
                <defs>
                    <linearGradient id="glow-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.2" />
                        <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="main-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#60a5fa" />
                        <stop offset="100%" stopColor="#8b5cf6" />
                    </linearGradient>
                </defs>

                <motion.circle
                    cx="50" cy="50" r="45"
                    fill="url(#glow-grad)"
                    variants={{
                        animate: { scale: [1, 1.05, 1], opacity: [0.3, 0.5, 0.3] }
                    }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                />

                {/* Stylized 'F' and 'S' combined in a wave */}
                {/* The 'F' vertical bar */}
                <motion.path
                    d="M35 25V75"
                    stroke={isLight ? "#0f172a" : "#ffffff"}
                    strokeWidth="8"
                    strokeLinecap="round"
                    variants={{
                        initial: { pathLength: 0 },
                        animate: { pathLength: 1 }
                    }}
                    transition={{ duration: 1, ease: "easeInOut" }}
                />

                {/* The 'F' top bar + 'S' upper curve */}
                <motion.path
                    d="M35 30H65C75 30 75 45 65 45H45C35 45 35 60 45 60H75"
                    stroke="url(#main-grad)"
                    strokeWidth="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    variants={{
                        initial: { pathLength: 0 },
                        animate: { pathLength: 1, transition: { delay: 0.5, duration: 1.5 } },
                        hover: { strokeWidth: 12 }
                    }}
                />

                {/* Flow Dots */}
                <motion.circle
                    cx="75" cy="60" r="4"
                    fill="#10b981"
                    animate={{ opacity: [0, 1, 0] }}
                    transition={{ duration: 2, repeat: Infinity, delay: 2 }}
                />
            </motion.svg>

            {showText && (
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 800,
                        letterSpacing: '-0.04em',
                        fontSize: current.fontSize,
                        color: isLight ? '#0f172a' : '#ffffff',
                        fontFamily: "'Inter', 'Outfit', sans-serif",
                        display: 'flex',
                        alignItems: 'baseline'
                    }}
                >
                    Flow
                    <Box component="span" sx={{ color: '#3b82f6' }}>State</Box>
                </Typography>
            )}
        </Box>
    );
};

export default BrandLogo;
